// NOTICE: KAPTION-INTEGRATION
// ai-connectors.CONSENT.3: turns the roles a person picks into "exactly what
// the AI tool gets": one row per area, read or change, and in which accounts.
// Mirrors the server: a role is a bundle of scopes (KaptionAccessToken::ROLES)
// and ADMIN_ONLY_SCOPES only apply where the person is an administrator.
// NOTICE: KAPTION-INTEGRATION - ai-connectors.CONSENT.5: every area carries
// an icon, shown on the role cards and in the "what it gets" table.

export const PERMISSION_AREAS = [
  {
    key: 'CONVERSATIONS',
    icon: 'i-lucide-messages-square',
    read: ['conversations:read', 'messages:read'],
    write: ['conversations:write', 'messages:write'],
  },
  {
    key: 'CONTACTS',
    icon: 'i-lucide-contact',
    read: ['contacts:read'],
    write: ['contacts:write'],
  },
  {
    key: 'CAMPAIGNS',
    icon: 'i-lucide-megaphone',
    read: ['campaigns:read', 'whatsapp_templates:read'],
    write: ['campaigns:write', 'whatsapp_templates:write'],
  },
  {
    key: 'REPORTS',
    icon: 'i-lucide-chart-column',
    read: ['reports:read'],
    write: [],
  },
  {
    key: 'ASSISTANTS',
    icon: 'i-lucide-sparkles',
    read: ['ai_studio:read'],
    write: ['ai_studio:write'],
  },
  // NOTICE: KAPTION-INTEGRATION - app-studio.CONSENT.3: apps:publish is
  // admin-only, so an agent account shows "read and change" via apps:write.
  {
    key: 'APPS',
    icon: 'i-lucide-blocks',
    read: ['apps:read'],
    write: ['apps:write', 'apps:publish'],
  },
  {
    key: 'SETUP',
    icon: 'i-lucide-inbox',
    read: [
      'inboxes:read',
      'labels:read',
      'custom_filters:read',
      'automation_rules:read',
    ],
    write: [
      'inboxes:write',
      'labels:write',
      'custom_filters:write',
      'automation_rules:write',
      'custom_attributes:write',
      'canned_responses:write',
      'macros:write',
    ],
  },
  {
    key: 'TEAMS',
    icon: 'i-lucide-users-round',
    read: ['teams:read'],
    write: ['teams:write'],
  },
  {
    key: 'PEOPLE',
    icon: 'i-lucide-user-cog',
    read: ['agents:read'],
    write: ['agents:write'],
  },
  {
    key: 'WEBHOOKS',
    icon: 'i-lucide-webhook',
    read: ['webhooks:read', 'agent_bots:read'],
    write: ['webhooks:write', 'agent_bots:write'],
  },
];

const RANK = { change: 2, read: 1 };

export const scopesForRoles = (roleKeys, rolesCatalog) => {
  const out = new Set();
  roleKeys.forEach(key => {
    (rolesCatalog[key]?.scopes || []).forEach(scope => out.add(scope));
  });
  return out;
};

const levelFor = (area, has) => {
  if (area.write.some(has)) return 'change';
  if (area.read.some(has)) return 'read';
  return null;
};

// Returns [{ key, level, adminLevel, agentLevel, where }] where `where` is
// 'everywhere' | 'admin_accounts' | 'nowhere', and `adminAccounts` lists the
// chosen accounts in which the higher level applies.
export const permissionRows = ({
  roleKeys,
  rolesCatalog,
  adminOnlyScopes = [],
  accounts = [],
}) => {
  const scopes = scopesForRoles(roleKeys, rolesCatalog);
  const adminOnly = new Set(adminOnlyScopes);
  const all = scopes.has('all');
  const hasAsAdmin = scope =>
    all || scopes.has(scope) || (scopes.has('read') && scope.endsWith(':read'));
  const hasAsAgent = scope => !adminOnly.has(scope) && hasAsAdmin(scope);
  const adminAccounts = accounts.filter(a => a.role === 'administrator');

  return PERMISSION_AREAS.map(area => {
    const adminLevel = levelFor(area, hasAsAdmin);
    const agentLevel = levelFor(area, hasAsAgent);
    let where = 'everywhere';
    let level = adminLevel;
    if (!adminLevel) where = 'nowhere';
    else if ((RANK[adminLevel] || 0) > (RANK[agentLevel] || 0)) {
      if (adminAccounts.length === accounts.length) where = 'everywhere';
      else if (adminAccounts.length) where = 'admin_accounts';
      else {
        where = agentLevel ? 'everywhere' : 'nowhere';
        level = agentLevel;
      }
    }
    return {
      key: area.key,
      icon: area.icon,
      level,
      adminLevel,
      agentLevel,
      where,
      adminAccounts,
    };
  });
};

// Roles that only take effect where the person is an administrator.
export const adminOnlyRoleKeys = rolesCatalog =>
  Object.entries(rolesCatalog)
    .filter(([, role]) => role.admin_only)
    .map(([key]) => key);

// ai-connectors.CONSENT.5: the areas one role opens, with read or change, for
// the icon chips on its card. Admin-only scopes count: the card describes the
// role itself, and the table on step 2 says where it applies.
export const areasForRole = (roleKey, rolesCatalog) => {
  const scopes = scopesForRoles([roleKey], rolesCatalog);
  const all = scopes.has('all');
  const has = scope =>
    all || scopes.has(scope) || (scopes.has('read') && scope.endsWith(':read'));
  return PERMISSION_AREAS.map(area => ({
    key: area.key,
    icon: area.icon,
    level: levelFor(area, has),
  })).filter(area => area.level);
};
